import { z } from "zod";

/**
 * Shared `validateSearch` field groups for the paginated / sortable list
 * routes. Every list page was re-declaring the exact same `page`,
 * `pageSize`, `sort` and `dir` zod fields; these spreads keep that
 * contract in one place so the client mirror of the server's
 * `parsePagination` clamp can't drift.
 *
 * Usage:
 *   const schema = z.object({
 *     q: z.string().optional(),
 *     ...sortSearchFields(SORT_KEYS),
 *     ...paginationSearchFields,
 *   });
 */

/** `?page` / `?pageSize` — coerced from the URL, page ≥ 1, size ∈ [1, 200]. */
export const paginationSearchFields = {
  page: z.coerce.number().int().min(1).optional(),
  pageSize: z.coerce.number().int().min(1).max(200).optional(),
};

/**
 * `?sort` / `?dir` for a route's own column keys. Pass the route's
 * `SORT_KEYS` tuple (declared `as const`); `sort` is constrained to
 * those keys and `dir` is the shared asc/desc pair.
 */
export function sortSearchFields<T extends readonly [string, ...string[]]>(keys: T) {
  return {
    sort: z.enum(keys).optional(),
    dir: z.enum(["asc", "desc"]).optional(),
  };
}
